import type {
  FillStyle,
  Gradient,
  LineStyle,
  Paragraph,
  TextLine,
  TextRun,
} from '@/api/types/stowed-element'

export function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  return value as Record<string, unknown>
}

export function asArray(value: unknown): unknown[] | null {
  return Array.isArray(value) ? value : null
}

export function finiteNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value === 'string' && value !== '') {
    const n = Number(value)

    return Number.isFinite(n) ? n : null
  }

  return null
}

export function parseGradient(raw: unknown): Gradient | undefined {
  const r = asRecord(raw)

  if (!r) return undefined

  return {
    startColor: finiteNumber(r.startColor ?? r.StartColor) ?? undefined,
    endColor: finiteNumber(r.endColor ?? r.EndColor) ?? undefined,
    gradientStyle:
      typeof r.gradientStyle === 'string'
        ? r.gradientStyle
        : typeof r.GradientStyle === 'string'
          ? r.GradientStyle
          : undefined,
  }
}

export function parseLineStyle(raw: unknown): LineStyle | undefined {
  const r = asRecord(raw)

  if (!r) return undefined

  return {
    weight: finiteNumber(r.weight ?? r.Weight) ?? undefined,
    colorId: finiteNumber(r.colorId ?? r.ColorId) ?? undefined,
    patternId: finiteNumber(r.patternId ?? r.PatternId) ?? undefined,
    tint: finiteNumber(r.tint ?? r.Tint) ?? undefined,
  }
}

export function parseFillStyle(raw: unknown): FillStyle | undefined {
  const r = asRecord(raw)

  if (!r) return undefined

  return {
    colorId: finiteNumber(r.colorId ?? r.ColorId) ?? undefined,
    tint: finiteNumber(r.tint ?? r.Tint) ?? undefined,
    gradient: parseGradient(r.gradient ?? r.Gradient),
  }
}

export function parseTextRun(raw: unknown): TextRun | null {
  const r = asRecord(raw)

  if (!r) return null

  return {
    textContent: typeof r.textContent === 'string' ? r.textContent : undefined,
    textAdvance: finiteNumber(r.textAdvance) ?? undefined,
    baseline: finiteNumber(r.baseline) ?? undefined,
    textWidth: finiteNumber(r.textWidth) ?? undefined,
    fontId: finiteNumber(r.fontId) ?? undefined,
    fontFamilyId: finiteNumber(r.fontFamilyId) ?? undefined,
    pointSize: finiteNumber(r.pointSize) ?? undefined,
    underline: typeof r.underline === 'boolean' ? r.underline : undefined,
    strikethrough: typeof r.strikethrough === 'boolean' ? r.strikethrough : undefined,
    kerning: finiteNumber(r.kerning) ?? undefined,
    tracking: finiteNumber(r.tracking) ?? undefined,
    leading: typeof r.leading === 'string' ? r.leading : undefined,
    // Round-tripped so yb360-authored colors survive load → edit → re-save.
    lineStyle: parseLineStyle(r.lineStyle ?? r.LineStyle),
    fillStyle: parseFillStyle(r.fillStyle ?? r.FillStyle),
  }
}

export function parseTextLine(raw: unknown): TextLine | null {
  const r = asRecord(raw)

  if (!r) return null

  const textRunsRaw = asArray(r.textRuns) ?? asArray(r.TextRuns)
  const textRuns: TextRun[] = []

  if (textRunsRaw) {
    for (const tr of textRunsRaw) {
      const parsed = parseTextRun(tr)

      if (parsed) textRuns.push(parsed)
    }
  }

  return {
    leftMargin: finiteNumber(r.leftMargin) ?? undefined,
    rightMargin: finiteNumber(r.rightMargin) ?? undefined,
    textWidth: finiteNumber(r.textWidth) ?? undefined,
    isHidden: typeof r.isHidden === 'boolean' ? r.isHidden : undefined,
    descent: finiteNumber(r.descent) ?? undefined,
    textRuns: textRuns.length > 0 ? textRuns : undefined,
  }
}

/** Parses a single wire paragraph (camelCase or PascalCase fields, numeric strings coerced) —
 *  shared by every read path that decodes `content.text.paragraphs`: checkpoint load, live
 *  multiplayer updates, and the fallback `editorShape.paragraphs` echo. */
export function parseParagraph(raw: unknown): Paragraph | null {
  const r = asRecord(raw)

  if (!r) return null

  const textLinesRaw = asArray(r.textLines) ?? asArray(r.TextLines)
  const textLines: TextLine[] = []

  if (textLinesRaw) {
    for (const tl of textLinesRaw) {
      const parsed = parseTextLine(tl)

      if (parsed) textLines.push(parsed)
    }
  }

  return {
    alignment: finiteNumber(r.alignment) ?? undefined,
    spaceAfter: finiteNumber(r.spaceAfter) ?? undefined,
    spaceBefore: finiteNumber(r.spaceBefore) ?? undefined,
    style: finiteNumber(r.style) ?? undefined,
    indentation: finiteNumber(r.indentation) ?? undefined,
    dropCap: typeof r.dropCap === 'string' ? r.dropCap : undefined,
    dropCapLines: finiteNumber(r.dropCapLines) ?? undefined,
    dropCapOffset: finiteNumber(r.dropCapOffset) ?? undefined,
    textLines: textLines.length > 0 ? textLines : undefined,
  }
}
